"use strict";
/**
 * 三层 Token。
 *
 * 为什么必须是三层，而不是"一堆 CSS 变量"：
 *   L1 Primitive 是调色板（brand.600 到底是什么色）——只换品牌色时改这里；
 *   L2 Semantic 是语义（正文用什么色、边框用什么色）——组件只允许引用这一层；
 *   L3 Component 是组件级微调（按钮圆角、卡片内边距）——变体与派生主题改这里。
 *
 * 关键纪律：**组件代码只能引用 L2/L3 的 CSS 变量，永远不引用 L1**。
 * 一旦某个组件直接写 var(--brand-600)，客户换品牌色时它就不跟着变，
 * 这种漏网之鱼在 10 个主题 × 100 个组件里几乎无法人工排查——所以有 ESLint 兜着。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_THEME = exports.THEMES = exports.nocturneTheme = exports.kilnTheme = exports.meridianTheme = exports.auroraTheme = exports.ARCHETYPE_LABEL = exports.DEFAULT_ARCHETYPE = exports.THEME_ARCHETYPES = void 0;
exports.isThemeArchetype = isThemeArchetype;
const layouts_1 = require("./layouts");
/**
 * 骨架原型。
 *
 * 这是一个**封闭枚举**：主题只能填一个名字，填不了 CSS 也带不了 JSX。
 *
 * 为什么需要有这一层：令牌能改尺寸与颜色，改不了"图在左还是右、卡片有没有边框、
 * 区块之间是留白还是细线"。只有令牌层的时候，两套主题选了同一个默认变体
 * 就长得一模一样 —— 这不是审美判断，是层级里根本没有"骨架"这个位置。
 * 实测症状是客户装完站换主题，发现只有颜色变了。
 *
 * 为什么不放开来让主题自带组件（布局级）：那要进构建、要分发构建产物，
 * 第三方主题场景下等于 RCE。三种骨架全部能用 CSS 表达，够用，且零风险。
 */
exports.THEME_ARCHETYPES = ['split', 'editorial', 'showcase'];
exports.DEFAULT_ARCHETYPE = 'split';
function isThemeArchetype(value) {
    return typeof value === 'string' && exports.THEME_ARCHETYPES.includes(value);
}
/**
 * 原型的用户可见名字。
 *
 * 放这里而不是各界面各写一份：控制台的主题卡、主题市场的筛选、后台的审核页
 * 迟早都会需要它，三处各写一份就会出现"同一个骨架叫三种名字"。
 */
exports.ARCHETYPE_LABEL = {
    split: { zh: '左文右图', en: 'Split' },
    editorial: { zh: '图在下方', en: 'Editorial' },
    showcase: { zh: '文字压图', en: 'Showcase' },
};
/**
 * aurora 主题。基于组件库改造而来，视觉差异靠令牌与变体堆出来，
 * 而不是靠"每个客户一套布局"——后者就是 L5 定制的入口，成本会复利。
 *
 * 性格：冷蓝、大圆角、留白多。适合科技、仪器、精密制造。
 */
exports.auroraTheme = {
    name: 'aurora',
    version: '1.0.0',
    archetype: 'split',
    layout: layouts_1.auroraLayout,
    label: { zh: '极光', en: 'Aurora' },
    description: {
        zh: '冷色科技感，大留白与大圆角。适合仪器、电子、精密制造。',
        en: 'Cool, technical, generous whitespace. Fits instruments, electronics, precision manufacturing.',
    },
    tokens: {
        primitive: {
            brand: {
                50: '#eef4ff',
                100: '#dbe7ff',
                300: '#93b4fd',
                500: '#2f6ef3',
                600: '#1f57d6',
                700: '#1743a8',
            },
            neutral: {
                0: '#ffffff',
                50: '#f7f9fc',
                100: '#eef2f7',
                200: '#dde4ee',
                400: '#98a4b6',
                600: '#5b6879',
                800: '#2b3440',
                900: '#161d26',
            },
            accent: { 500: '#e8a33d' },
            font: {
                sans: "'Inter', 'Helvetica Neue', Arial, 'Noto Sans SC', 'Noto Sans Arabic', sans-serif",
                serif: "Georgia, 'Noto Serif SC', serif",
            },
        },
        semantic: {
            color: {
                bg: '{neutral.0}',
                surface: '{neutral.50}',
                text: '{neutral.900}',
                textMuted: '{neutral.600}',
                border: '{neutral.200}',
                brand: '{brand.600}',
                brandHover: '{brand.700}',
                brandSubtle: '{brand.50}',
                onBrand: '{neutral.0}',
                accent: '{accent.500}',
                /** 页头底色。单独一条是因为有一套主题要"深色页头"，靠 surface 做不到 */
                headerBg: '{neutral.0}',
                headerText: '{neutral.900}',
                /** 页头里的强调色（当前导航项的下划线）。深色页头上品牌色会糊掉，所以单列 */
                headerAccent: '{brand.600}',
                /**
                 * 页头品牌方块。深色页头上直接压品牌色是一块糊在一起的深绿，
                 * 所以"方块底色 + 方块上的字色"也当成一对令牌交给主题决定。
                 */
                brandMark: '{brand.600}',
                onBrandMark: '{neutral.0}',
                /**
                 * 压在图片上的文字色。showcase 原型的首屏是整幅出血图、文字直接压图上，
                 * 这时候正文色（近黑）会糊进画面。单列一条是因为它和背景图的关系
                 * 与 bg/surface 无关，跟着主题走反而更稳。
                 */
                onMedia: '{neutral.0}',
            },
            font: {
                family: '{font.sans}',
                /** 标题字体。和正文字体分开，衬线标题是最省力的"风格开关" */
                heading: '{font.sans}',
                sizeBase: '16px',
                sizeSm: '14px',
                sizeLg: '18px',
                sizeXl: '28px',
                sizeHero: '44px',
                weightNormal: 400,
                weightBold: 700,
                lineHeight: 1.6,
                /**
                 * 标题字距与字重必须成令牌，不能写死在 CSS 里。
                 *
                 * 写死的后果：衬线标题配 -0.02em 会挤成一团，而 showcase 需要
                 * -0.03em + 800 字重才有张力。三条主题共用一套字距，等于
                 * "排版气质"这一层完全没有差异可用。
                 */
                trackingTitle: '-0.02em',
                weightHeading: 700,
                /** 小标签（eyebrow）的字距。全大写小字距窄了会糊，showcase 需要 0.18em */
                trackingEyebrow: '0.12em',
            },
            space: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '40px', section: '64px' },
            radius: { sm: '4px', md: '8px', lg: '16px', pill: '999px' },
            shadow: { card: '0 1px 2px rgba(22,29,38,0.06), 0 8px 24px rgba(22,29,38,0.06)' },
            layout: { container: '1200px', gutter: '24px' },
            /**
             * 图片比例。形态归原型、比例归令牌 —— 这条线看起来细，
             * 但 editorial 要 21:9 通栏、showcase 要 3:4 竖图，写死一个 4:3
             * 就等于三套主题的图长得一样。
             */
            media: { heroAspect: '4 / 3', cardAspect: '16 / 10' },
        },
        component: {
            button: { radius: '{radius.md}', paddingInline: '{space.lg}', paddingBlock: '{space.sm}' },
            card: { radius: '{radius.lg}', padding: '{space.lg}', border: '1px solid {color.border}' },
            header: { height: '72px' },
        },
    },
    variants: {
        classic: {
            key: 'classic',
            label: { zh: '经典两栏', en: 'Classic split' },
            tokens: { font: { sizeHero: '40px' } },
        },
        fullscreen: {
            key: 'fullscreen',
            label: { zh: '全屏首图', en: 'Fullscreen hero' },
            tokens: {
                font: { sizeHero: '56px' },
                layout: { container: '1320px' },
                // 图是主体，压成宽幅而不是 4:3。比例是尺寸，归令牌；
                // 原来这条写在 CSS 里，等于"变体改尺寸"这件事破了纪律
                media: { heroAspect: '21 / 9' },
                // 注意：变体里的键与 component 层同级（header.height），不是再嵌一层 component，
                // 否则会生成 --component-header-height 这种没人消费的变量。
                header: { height: '88px' },
            },
        },
        compact: {
            key: 'compact',
            label: { zh: '紧凑', en: 'Compact' },
            tokens: {
                font: { sizeHero: '34px' },
                space: { section: '48px' },
            },
        },
    },
};
/**
 * meridian 主题。
 *
 * 和 aurora 的差别不是"换个蓝色"，而是三件一眼能看出的事：
 *   1) **衬线标题** —— 从 `font.heading` 走，正文仍是无衬线；
 *   2) **直角小圆角 + 更密的间距** —— 工业感，不像 SaaS 后台；
 *   3) **深色页头** —— `color.headerBg` 反转，让站点看起来"有招牌"。
 *
 * 骨架是 `editorial`：标题在上、图在下方通栏 21:9、区块之间用细线而不是色块交替。
 * 这一点是 v1.1 补的 —— 之前它和 aurora 共用同一个骨架，只有颜色字体不同，
 * 客户装完站换过来会说"这不是一样吗"。配色和字体是叠加项，不是"不一样"的判据。
 *
 * 机床绿 + 砖红 + 纸白，适合五金、汽配、工业耗材这类外贸客户：
 * 老式机床就是这个绿，而砖红是防锈漆与安全标识的颜色。
 */
exports.meridianTheme = {
    name: 'meridian',
    version: '1.1.0',
    archetype: 'editorial',
    layout: layouts_1.meridianLayout,
    label: { zh: '子午线', en: 'Meridian' },
    description: {
        zh: '样本册式排版，衬线标题、图在文字下方、细线分节。适合五金、汽配、工业耗材。',
        en: 'Catalogue layout: serif headings, full-width image below the copy, hairline dividers. Fits hardware, auto parts, industrial supplies.',
    },
    tokens: {
        primitive: {
            brand: {
                50: '#eef6f3',
                100: '#d7e9e3',
                300: '#8dbfb0',
                500: '#1a7a63',
                600: '#0f5c4a',
                700: '#0a4436',
            },
            neutral: {
                0: '#ffffff',
                50: '#faf8f4',
                100: '#f2eee7',
                200: '#e2dbd0',
                400: '#a2968a',
                600: '#5f574d',
                800: '#2f2a24',
                900: '#1b1815',
            },
            // 砖红。原来是土金，但第三套主题（kiln）的强调色是铜橙，
            // 两套的金色放在一起会让人以为是"同一套换了个底"
            accent: { 500: '#9c3a2b' },
            font: {
                sans: "'Inter', 'Helvetica Neue', Arial, 'Noto Sans SC', 'Noto Sans Arabic', sans-serif",
                serif: "'Iowan Old Style', Georgia, 'Songti SC', 'Noto Serif SC', serif",
            },
        },
        semantic: {
            color: {
                bg: '{neutral.0}',
                surface: '{neutral.50}',
                text: '{neutral.900}',
                textMuted: '{neutral.600}',
                border: '{neutral.200}',
                brand: '{brand.600}',
                brandHover: '{brand.700}',
                brandSubtle: '{brand.50}',
                onBrand: '{neutral.0}',
                accent: '{accent.500}',
                // 深色页头。文字色必须一起换，否则就是"深底黑字"这种一眼可见的错误
                headerBg: '{neutral.800}',
                headerText: '{neutral.0}',
                // 金色下划线：深底上墨绿几乎看不见
                headerAccent: '{accent.500}',
                // 深色页头上品牌方块改用砖红底 + 白字，否则一块深绿糊在深棕上
                brandMark: '{accent.500}',
                onBrandMark: '{neutral.0}',
                onMedia: '{neutral.0}',
            },
            font: {
                family: '{font.sans}',
                heading: '{font.serif}',
                sizeBase: '16px',
                sizeSm: '14px',
                sizeLg: '17px',
                sizeXl: '26px',
                sizeHero: '52px',
                weightNormal: 400,
                weightBold: 600,
                lineHeight: 1.7,
                // 衬线字要正字距。写成 -0.02em 的话，宋体/Georgia 的字面会互相啃掉
                trackingTitle: '0em',
                weightHeading: 600,
                trackingEyebrow: '0.16em',
            },
            space: { xs: '4px', sm: '8px', md: '14px', lg: '22px', xl: '36px', section: '56px' },
            // 直角。pill 也压到 2px —— 胶囊按钮和这套版式放在一起会很突兀
            radius: { sm: '2px', md: '3px', lg: '4px', pill: '2px' },
            shadow: { card: '0 1px 0 rgba(27,24,21,0.06)' },
            layout: { container: '1180px', gutter: '24px' },
            // 通栏 21:9：图在文字下方占满整栏，是这个原型最容易认出来的特征
            media: { heroAspect: '21 / 9', cardAspect: '4 / 3' },
        },
        component: {
            button: { radius: '{radius.md}', paddingInline: '{space.lg}', paddingBlock: '{space.sm}' },
            card: { radius: '{radius.lg}', padding: '{space.lg}', border: '1px solid {color.border}' },
            header: { height: '64px' },
        },
    },
    /**
     * 变体只调字号、间距、容器宽，**不允许改构图**。
     *
     * 原来叫 `editorial` / `mirror`：前者只是改尺寸（名字却像个骨架），
     * 后者靠 `order: -1` 把图翻到左边。骨架搬去 archetype 之后，
     * 这两个名字都会撒谎 —— 客户选「图左文右」，看到的却是图在下方。
     * 名字必须描述它真正做的事。
     */
    variants: {
        standard: {
            key: 'standard',
            label: { zh: '标准', en: 'Standard' },
            tokens: {
                font: { sizeHero: '52px' },
                layout: { container: '1140px' },
                header: { height: '76px' },
            },
        },
        wide: {
            key: 'wide',
            label: { zh: '宽版', en: 'Wide' },
            tokens: {
                font: { sizeHero: '46px' },
                layout: { container: '1260px' },
            },
        },
        compact: {
            key: 'compact',
            label: { zh: '紧凑', en: 'Compact' },
            tokens: {
                font: { sizeHero: '38px' },
                space: { section: '44px' },
                header: { height: '60px' },
            },
        },
    },
};
/**
 * kiln 主题（中文「窑变」）。
 *
 * 第三套，`showcase` 骨架：整幅出血的首屏图、文字压在图左下、页头透明浮在上面。
 * 它存在的理由不是"再来一套配色"，而是三套主题里必须有一套**敢把首屏做成一张图**——
 * 品牌客户愿意为这件事多付钱，而这恰恰是令牌层永远做不到的。
 *
 * 三个刻意的选择：
 *   1) **全直角**，但按钮是胶囊 —— 全直角页面里唯一的曲线，是它的签名；
 *   2) **标题 800 字重 + -0.03em 字距**，和 meridian 的衬线正字距完全相反；
 *   3) **96px 区块留白**，比另外两套大一半，靠留白而不是色块分隔。
 */
exports.kilnTheme = {
    name: 'kiln',
    version: '1.0.0',
    archetype: 'showcase',
    layout: layouts_1.kilnLayout,
    label: { zh: '窑变', en: 'Kiln' },
    description: {
        zh: '整幅出血首屏，大字重、全直角、大留白。适合品牌客户与新品首发。',
        en: 'Full-bleed hero, heavy type, no radius, generous whitespace. For brand launches.',
    },
    tokens: {
        primitive: {
            // 铜橙。600 比 accent 深一档：白字按钮要过 4.5:1，而 #c2683a 配白字只有 3.94
            brand: {
                50: '#fdf3ed',
                100: '#f9e2d5',
                300: '#e0a887',
                500: '#b45d33',
                600: '#a2532c',
                700: '#87431f',
            },
            neutral: {
                0: '#ffffff',
                50: '#fbfaf7',
                100: '#f1eee8',
                200: '#ddd8cf',
                400: '#9a938a',
                600: '#57534c',
                800: '#2a2724',
                900: '#101211',
            },
            accent: { 500: '#c2683a' },
            font: {
                sans: "'Inter', 'Helvetica Neue', Arial, 'Noto Sans SC', 'Noto Sans Arabic', sans-serif",
                serif: "Georgia, 'Noto Serif SC', serif",
            },
        },
        semantic: {
            color: {
                bg: '{neutral.50}',
                surface: '{neutral.100}',
                text: '{neutral.900}',
                textMuted: '{neutral.600}',
                border: '{neutral.200}',
                brand: '{brand.600}',
                brandHover: '{brand.700}',
                brandSubtle: '{brand.50}',
                onBrand: '{neutral.0}',
                accent: '{accent.500}',
                // 页头压在首屏图上，文字必须是白的；离开首页（商品页等）时
                // 页头落回这条深底色，导航在浅底上依然看得见
                headerBg: '{neutral.900}',
                headerText: '{neutral.0}',
                headerAccent: '{accent.500}',
                brandMark: '{accent.500}',
                onBrandMark: '{neutral.900}',
                onMedia: '{neutral.0}',
            },
            font: {
                family: '{font.sans}',
                heading: '{font.sans}',
                sizeBase: '16px',
                sizeSm: '14px',
                sizeLg: '19px',
                sizeXl: '32px',
                sizeHero: '66px',
                weightNormal: 400,
                weightBold: 700,
                lineHeight: 1.55,
                trackingTitle: '-0.03em',
                weightHeading: 800,
                trackingEyebrow: '0.18em',
            },
            space: { xs: '4px', sm: '8px', md: '16px', lg: '28px', xl: '56px', section: '96px' },
            // 全直角。pill 是唯一的曲线，按钮走它
            radius: { sm: '0px', md: '0px', lg: '0px', pill: '999px' },
            shadow: { card: 'none' },
            layout: { container: '1280px', gutter: '28px' },
            // 竖图 3:4：双列大图是这个原型"靠图说话"的具体形态
            media: { heroAspect: '16 / 9', cardAspect: '3 / 4' },
        },
        component: {
            button: { radius: '{radius.pill}', paddingInline: '{space.lg}', paddingBlock: '14px' },
            card: { radius: '{radius.lg}', padding: '{space.lg}', border: '0' },
            header: { height: '88px' },
        },
    },
    variants: {
        standard: {
            key: 'standard',
            label: { zh: '标准', en: 'Standard' },
            tokens: { layout: { container: '1280px' } },
        },
        wide: {
            key: 'wide',
            label: { zh: '宽版', en: 'Wide' },
            tokens: {
                font: { sizeHero: '76px' },
                layout: { container: '1440px' },
            },
        },
        compact: {
            key: 'compact',
            label: { zh: '紧凑', en: 'Compact' },
            tokens: {
                font: { sizeHero: '52px' },
                space: { section: '64px' },
                header: { height: '72px' },
            },
        },
    },
};
/**
 * nocturne（夜曲）。**付费主题 / 订阅制的机制验证样板**。
 *
 * 它在产品里的角色是"第一件能卖的商品"，不是最终设计：
 * 锁、订阅、到期降级、退订回落这四条链路必须有真实商品才测得出来，
 * 而基础三套是免费的，永远走不到那些分支。设计同事的正式付费主题
 * 产出后，**换掉这一条的令牌即可**，订阅数据与接口都不用改
 * （订阅记的是 `theme_name`，改名意味着存量订阅失效，所以换主题时
 * 宁可保留 name 只换视觉，也不要随手改 name）。
 *
 * **当前不对外陈列**（`listed: false`，2026-10-30 产品决定：市场只留基础三套）。
 * 它留在注册表里是因为：订阅链路、到期降级、退订回落的测试都拿它当付费样本；
 * 真删掉这些测试就无处落脚。市场不展示它，但已经订阅过它的客户仍能渲染。
 *
 * 性格：全站深色、青绿电光、大圆角。适合消费电子、照明、音响 ——
 * 与前三套的"浅底 + 中性色"形成明显区分，客户一眼能看出这是另一档东西。
 */
exports.nocturneTheme = {
    name: 'nocturne',
    version: '1.0.0',
    listed: false,
    archetype: 'showcase',
    label: { zh: '夜曲', en: 'Nocturne' },
    description: {
        zh: '全站深色，青绿电光强调，大圆角。适合消费电子、照明、音响。',
        en: 'Dark throughout with electric teal accents. For consumer electronics, lighting, audio.',
    },
    pricing: { monthlyCents: 12900 },
    tokens: {
        primitive: {
            brand: {
                50: '#e6fbf5',
                100: '#c2f5e6',
                300: '#6ee9c7',
                500: '#19d3a2',
                600: '#10b389',
                700: '#0a8a69',
            },
            neutral: {
                0: '#ffffff',
                50: '#f5f8fb',
                100: '#e8eef5',
                200: '#c9d5e2',
                400: '#8a99ab',
                600: '#5a6b80',
                700: '#2b3546',
                800: '#1d2634',
                900: '#11161f',
            },
            accent: { 500: '#ff7a59' },
            font: {
                sans: "'Inter', 'Helvetica Neue', Arial, 'Noto Sans SC', 'Noto Sans Arabic', sans-serif",
                serif: "Georgia, 'Noto Serif SC', serif",
            },
        },
        semantic: {
            color: {
                // 全站深色：正文与卡片都落在深色区间，靠 800 与 900 的差分层
                bg: '{neutral.900}',
                surface: '{neutral.800}',
                text: '{neutral.50}',
                textMuted: '{neutral.400}',
                border: '{neutral.700}',
                brand: '{brand.600}',
                // 深色底上"悬停更亮"才符合直觉，与浅色主题的方向相反
                brandHover: '{brand.500}',
                brandSubtle: '{neutral.800}',
                // 青绿按钮上压深字：白字配 #10b389 只有 2.3:1，会糊
                onBrand: '{neutral.900}',
                accent: '{accent.500}',
                // 页头比正文亮一档，否则深色页头与深色背景之间没有边界
                headerBg: '{neutral.800}',
                headerText: '{neutral.50}',
                headerAccent: '{brand.500}',
                brandMark: '{brand.500}',
                onBrandMark: '{neutral.900}',
                onMedia: '{neutral.0}',
            },
            font: {
                family: '{font.sans}',
                heading: '{font.sans}',
                sizeBase: '16px',
                sizeSm: '14px',
                sizeLg: '19px',
                sizeXl: '32px',
                sizeHero: '64px',
                weightNormal: 400,
                weightBold: 700,
                lineHeight: 1.6,
                trackingTitle: '-0.025em',
                weightHeading: 800,
                trackingEyebrow: '0.16em',
            },
            space: { xs: '4px', sm: '8px', md: '16px', lg: '28px', xl: '56px', section: '92px' },
            radius: { sm: '8px', md: '12px', lg: '16px', pill: '999px' },
            // 深色底上浅色投影不生效，只能靠更深的黑拉开层次
            shadow: { card: '0 18px 40px rgba(0, 0, 0, 0.45)' },
            layout: { container: '1240px', gutter: '24px' },
            media: { heroAspect: '16 / 9', cardAspect: '4 / 5' },
        },
        component: {
            button: { radius: '{radius.pill}', paddingInline: '{space.lg}', paddingBlock: '13px' },
            card: { radius: '{radius.lg}', padding: '{space.lg}', border: '1px' },
            header: { height: '76px' },
        },
    },
    variants: {
        spotlight: {
            key: 'spotlight',
            label: { zh: '聚光', en: 'Spotlight' },
            tokens: { layout: { container: '1240px' } },
        },
        cinematic: {
            key: 'cinematic',
            label: { zh: '宽幕', en: 'Cinematic' },
            tokens: {
                font: { sizeHero: '76px' },
                layout: { container: '1440px' },
            },
        },
        compact: {
            key: 'compact',
            label: { zh: '紧凑', en: 'Compact' },
            tokens: {
                font: { sizeHero: '50px' },
                space: { section: '64px' },
                header: { height: '64px' },
            },
        },
    },
    /**
     * 版式跟着**原型**走，不跟着主题走：夜曲与窑变同属 `showcase` 家族，
     * 段序一致、只换令牌。不给它写版式的话，客户在测试链路里订阅到夜曲，
     * 渲染会掉回"页头页脚硬编码"的老路径 —— 于是同一套原型下两套主题
     * 一个能编辑页头、一个不能，看起来像版式丢了。
     */
    layout: layouts_1.kilnLayout,
};
/**
 * 主题注册表。**新增主题只加在这里**，控制台的选择卡片、平台侧的校验、
 * 渲染层的解析都从这一份读 —— 三处各写一份清单一定会漂移，
 * 症状是"控制台能选，但保存被拒"或者反过来。
 */
exports.THEMES = {
    aurora: exports.auroraTheme,
    meridian: exports.meridianTheme,
    kiln: exports.kilnTheme,
    nocturne: exports.nocturneTheme,
};
/** 默认主题。库里 theme_name 是 NOT NULL default 'aurora'，这里保持一致 */
exports.DEFAULT_THEME = 'aurora';
//# sourceMappingURL=tokens.js.map